import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SignUpButton } from "@clerk/clerk-react";
import { Linkedin, Twitter, Github, Instagram, Facebook, Youtube, Music, Globe, Link as LinkIcon, Mail, Phone, ArrowRight } from "lucide-react";
import QRCode from "qrcode";
import { getResumeUrlByUsername, ContactFormConfig, LinkType } from "../utils/api";
import { themeStyles } from '../utils/cardThemes';

const SocialIcon: React.FC<{ name: string; className?: string }> = ({ name, className = "w-5 h-5" }) => {
  const icons: Record<string, React.ReactNode> = {
    linkedin: <Linkedin className={className} />,
    twitter: <Twitter className={className} />,
    github: <Github className={className} />,
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    youtube: <Youtube className={className} />,
    tiktok: <Music className={className} />,
    globe: <Globe className={className} />,
    link: <LinkIcon className={className} />,
    mail: <Mail className={className} />,
    phone: <Phone className={className} />,
  };
  return <>{icons[name] || <LinkIcon className={className} />}</>;
};

export interface CardRendererLink {
  id: string;
  title: string;
  url: string;
  description?: string;
  icon?: string;
  category?: string;
  customIcon?: string;
  order: number;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogSiteName?: string;
  /** 'url' (default) or 'contact_form'. Contact-form links render an inline
   *  form on the public card instead of navigating away. */
  linkType?: LinkType;
  /** Present when linkType === 'contact_form'. */
  formConfig?: ContactFormConfig | null;
}

export interface CardRendererCard {
  id: string;
  userId: string;
  username: string;
  name: string;
  position: string;
  email: string;
  phoneNumber?: string;
  websiteUrl?: string;
  company?: string;
  wideLogo?: string;
  squareLogo?: string;
  logoStyle?: string;
  showProBadge?: boolean;
  badgeStyle?: string;
  theme?: string;
  profilePhoto?: string;
  hasResume?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showContactBox?: boolean;
  showAddress?: boolean;
  vcfIncludeEmail?: boolean;
  vcfIncludePhone?: boolean;
  vcfIncludeAddress?: boolean;
  vcfIncludeWebsite?: boolean;
}

// The legacy generic-Connect form was removed — owners now configure their
// own contact form as a contact_form-typed link with custom fields. The
// ConnectFormData type and onConnectSubmit prop are gone with it.
interface CardRendererProps {
  card: CardRendererCard;
  links: CardRendererLink[];
  bio?: string;
  address?: string;
  isPro?: boolean;
  resolvedTheme?: string;
  preview?: boolean;
  isSignedIn?: boolean;
  onLinkClick?: (link: CardRendererLink) => void;
}

// Each theme now carries both light and dark Tailwind classes. Dark variants
// activate when the document has the `dark` class (Tailwind `darkMode: 'class'`
// — set globally by the WhoBrew dark-mode toggle, or by the visitor's system
// preference if we wire that up later). Owners pick a theme; the visitor sees
// it in whichever mode their environment prefers.

const getSmartUrl = (url: string): string => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) return url;
  if (url.includes('linkedin.com/in/')) {
    const u = url.split('linkedin.com/in/')[1]?.split(/[/?#]/)[0];
    if (u) return `linkedin://in/${u}`;
  }
  if (url.includes('instagram.com/')) {
    const u = url.split('instagram.com/')[1]?.split(/[/?#]/)[0];
    if (u) return `instagram://user?username=${u}`;
  }
  if (url.includes('twitter.com/') || url.includes('x.com/')) {
    const u = url.split(/(?:twitter|x)\.com\//)[1]?.split(/[/?#]/)[0];
    if (u) return `twitter://user?screen_name=${u}`;
  }
  if (url.includes('facebook.com/')) {
    return `fb://facewebmodal/f?href=${encodeURIComponent(url)}`;
  }
  if (url.includes('tiktok.com/@')) {
    const u = url.split('tiktok.com/@')[1]?.split(/[/?#]/)[0];
    if (u) return `tiktok://user?username=${u}`;
  }
  return url;
};

// ─── Contact-form link rendering ─────────────────────────────────────────────
//
// Contact-form links no longer expand inline. They render as a navigation
// button that routes to /card/:username/form/:linkId — a dedicated page that
// works in both the public-card flow AND in the My Card editor preview (the
// inline form was preview-mode blocked, which left owners unable to test their
// own form). See src/components/PublicCardForm.tsx for the destination page.

type ThemeStyle = typeof themeStyles[string];


// ─── CardQR — "Save my card" panel ─────────────────────────────────────────
//
// Renders a QR code on a white tile next to a "Save my card / Scan with your
// phone" label. The whole panel is a clickable anchor — tapping it opens the
// public card URL in a new tab so admins viewing the editor preview can jump
// to the live page in one click. The QR itself is white-on-dark regardless of
// theme so any scanner picks it up reliably; the surrounding panel border and
// label colors follow the active theme.

const CardQR: React.FC<{ url: string; theme: ThemeStyle }> = ({ url, theme }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !url) return;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 56,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#1f1410', light: '#ffffff' },
    }).catch(() => {
      // Silent fail — the panel will still render with an empty canvas.
    });
  }, [url]);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-3 p-3 rounded-lg border ${theme.text} border-opacity-15 hover:bg-current/5 transition-colors`}
      aria-label="Open my public card in a new tab"
    >
      <div className="bg-white p-1 rounded flex-shrink-0">
        <canvas ref={canvasRef} className="block" aria-label="QR code to save this card" />
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${theme.text}`}>Save my card<span className="text-accent-500">.</span></p>
        <p className={`text-xs ${theme.subtext}`}>Scan with your phone, or tap to open.</p>
      </div>
    </a>
  );
};

const CardRenderer: React.FC<CardRendererProps> = ({
  card,
  links,
  bio = '',
  address = '',
  isPro = false,
  resolvedTheme,
  preview = false,
  isSignedIn = false,
  onLinkClick,
}) => {
  const [copied, setCopied] = useState(false);

  const themeKey = resolvedTheme || card.theme || 'default';
  const currentTheme = themeStyles[themeKey] || themeStyles.default;

  const downloadVCard = () => {
    const nameParts = card.name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const vcard = [
      'BEGIN:VCARD', 'VERSION:3.0',
      `FN:${card.name}`,
      `N:${lastName};${firstName};;;`,
      card.position ? `TITLE:${card.position}` : '',
      card.company ? `ORG:${card.company}` : '',
      card.vcfIncludeEmail !== false && card.email ? `EMAIL;TYPE=WORK:${card.email}` : '',
      card.vcfIncludePhone !== false && card.phoneNumber ? `TEL;TYPE=CELL:${card.phoneNumber}` : '',
      card.vcfIncludeWebsite !== false && card.websiteUrl ? `URL:${card.websiteUrl}` : '',
      card.vcfIncludeAddress !== false && address ? `ADR;TYPE=WORK:;;${address};;;;` : '',
      'NOTE:WhoBrew Digital Business Card',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${card.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const shareUrl = preview
      ? `${window.location.origin}/card/${card.username}`
      : window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${card.name} - WhoBrew Card`, text: `Check out ${card.name}'s digital business card`, url: shareUrl });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') await copyToClipboard(shareUrl);
      }
    } else {
      await copyToClipboard(shareUrl);
    }
  };

  const handleLinkAnchorClick = (e: React.MouseEvent, link: CardRendererLink) => {
    // Defensive: if a link somehow renders through this anchor path with no
    // URL (e.g. a contact_form link whose linkType didn't make it onto the
    // row), block navigation. Without this, clicking opens the current page
    // in a new tab and looks like a refresh.
    if (!link.url || !link.url.trim()) {
      e.preventDefault();
      return;
    }
    if (onLinkClick) onLinkClick(link);
    const smartUrl = getSmartUrl(link.url);
    if (smartUrl !== link.url) {
      e.preventDefault();
      const timeout = setTimeout(() => { window.open(link.url, '_blank'); }, 500);
      window.location.href = smartUrl;
      window.addEventListener('blur', () => clearTimeout(timeout), { once: true });
    }
  };

  return (
    <div className={`min-h-full ${currentTheme.bg}`}>
      {/* Card boundary — a rounded, theme-aware outline so the content reads as
          a distinct "card" against the themed page background. Kept as a
          border + ring + subtle glass tint (rather than an opaque theme.card
          fill) so the inner Links tiles and Contact box — which already use the
          theme's card/linkBg surface — keep their own definition instead of
          blending into the container. The neutral black/white border tints give
          contrast on every theme's bg in BOTH light and dark mode (paired
          `dark:` variants). */}
      <div className="max-w-md mx-auto my-6 sm:my-8 px-6 py-8 rounded-3xl bg-white/50 dark:bg-white/5 border border-black/10 dark:border-white/15 ring-1 ring-black/5 dark:ring-white/10 shadow-coffee-lg backdrop-blur-sm">
        {/* Header — editorial layout (Option C). No cover strip. Avatar
            inline next to the name, like a magazine byline. QR code below
            the bio in a "Save my card" panel that travels across themes. */}
        <div className="mb-8 relative">
          {isPro && card.showProBadge !== false && (() => {
            const badgeStyles: Record<string, string> = {
              gold: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-900',
              platinum: 'bg-gradient-to-r from-gray-300 to-gray-100 text-gray-700',
              dark: 'bg-gradient-to-r from-gray-800 to-gray-900 text-gray-100',
            };
            const style = badgeStyles[card.badgeStyle || 'gold'] || badgeStyles.gold;
            return (
              <span className={`absolute top-0 right-0 z-20 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${style}`}>PRO</span>
            );
          })()}

          {/* Optional wide-logo banner — kept for users who set logoStyle=wide.
              Sits as a thin strip above the byline rather than a colored cover. */}
          {card.wideLogo && card.logoStyle === 'wide' && (
            <div className={`mb-4 pb-3 border-b ${currentTheme.text} border-opacity-10`}>
              <img src={card.wideLogo} alt={`${card.name} logo`} className="h-10 mx-auto object-contain" />
            </div>
          )}

          {/* Inline byline — avatar on the left, name + position right */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 ${currentTheme.accent} rounded-full flex-shrink-0 flex items-center justify-center ring-2 ring-white shadow overflow-hidden`}>
              {card.squareLogo ? (
                <img src={card.squareLogo} alt={`${card.name} logo`} className="w-full h-full object-contain p-1" />
              ) : card.profilePhoto ? (
                <img src={card.profilePhoto} alt={`${card.name} profile`} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xl font-bold">
                  {card.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className={`text-2xl sm:text-3xl font-bold ${currentTheme.text} tracking-tight leading-tight`}>
                {card.name}
              </h1>
              <p className={`${currentTheme.text} opacity-70 text-sm mt-0.5`}>
                {card.position}{card.company ? ` · ${card.company}` : ''}
              </p>
            </div>
          </div>

          {bio && (
            <p className={`${currentTheme.subtext} text-sm italic mb-4`}>{bio}</p>
          )}
        </div>

        {/* Links — moved above Contact Info so they're the first thing
            visitors see after the byline. */}
        {links.length > 0 ? (
          <div className="space-y-3">
            <h2 className={`text-lg font-semibold ${currentTheme.text} text-center mb-4`}>My Links</h2>
            {/* Categories are an editor-side organization tool only — they're
                no longer surfaced as headers on the public card. Links render
                in their saved order. */}
            {links.map((link) => {
              // Contact-form link: route to a dedicated form page instead of
              // expanding inline. Detect by EITHER explicit linkType=='contact_form'
              // OR the presence of a formConfig — a row created via the editor
              // always carries one, so this catches links whose linkType column
              // wasn't populated for whatever reason. See PublicCardForm.tsx.
              const isContactForm =
                link.linkType === 'contact_form' || !!link.formConfig;
              if (isContactForm) {
                const buttonLabel = link.formConfig?.buttonLabel || link.title || 'Contact me';
                return (
                  <Link
                    key={link.id}
                    to={`/card/${card.username}/form/${link.id}`}
                    onClick={() => { if (onLinkClick) onLinkClick(link); }}
                    className={`block ${currentTheme.linkBg} rounded-2xl shadow-coffee-lg overflow-hidden hover:shadow-coffee-xl transition-all duration-200 hover:scale-[1.02] hover:bg-coffee-50 border-l-[3px] border-accent-500 hover:border-accent-700 mb-3`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      <div className={`w-12 h-12 ${currentTheme.accent} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold ${currentTheme.linkText} truncate`}>{buttonLabel}</h3>
                        {link.description && (
                          <p className={`text-sm ${currentTheme.subtext} opacity-70 truncate`}>{link.description}</p>
                        )}
                      </div>
                      <ArrowRight className={`w-4 h-4 ${currentTheme.subtext} opacity-60`} />
                    </div>
                  </Link>
                );
              }
              const hasPreview = !!link.ogImage;
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => handleLinkAnchorClick(e, link)}
                  className={`block ${currentTheme.linkBg} rounded-2xl shadow-coffee-lg overflow-hidden hover:shadow-coffee-xl transition-all duration-200 hover:scale-[1.02] hover:bg-coffee-50 border-l-[3px] border-coffee-300 hover:border-coffee-600 mb-3`}
                >
                  {hasPreview ? (
                    <div>
                      <div className="w-full h-32 bg-coffee-100 overflow-hidden">
                        <img src={link.ogImage} alt={link.ogTitle || link.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                      <div className="p-4">
                        <h3 className={`font-semibold ${currentTheme.linkText} truncate`}>{link.title}</h3>
                        {link.ogDescription && (
                          <p className={`text-sm ${currentTheme.subtext} mt-1 line-clamp-2`}>{link.ogDescription}</p>
                        )}
                        {link.ogSiteName && (
                          <p className={`text-xs ${currentTheme.subtext} opacity-60 mt-2`}>{link.ogSiteName}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 flex items-center gap-4">
                      <div className={`w-12 h-12 ${currentTheme.accent} rounded-full flex items-center justify-center flex-shrink-0`}>
                        {link.customIcon ? (
                          <img src={link.customIcon} alt="" className="w-5 h-5 rounded object-cover" />
                        ) : (
                          <SocialIcon name={link.icon || "link"} className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold ${currentTheme.linkText} truncate`}>{link.title}</h3>
                        {link.description && (
                          <p className={`text-sm ${currentTheme.subtext} opacity-70 truncate`}>{link.description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        ) : (
          <div className={`${currentTheme.card} rounded-2xl shadow-coffee-lg p-6 text-center`}>
            <p className={`${currentTheme.subtext} text-sm`}>This card doesn't have any links yet.</p>
          </div>
        )}

        {/* Contact Info — moved below Links per the new layout. */}
        {card.showContactBox !== false && (
          <div className={`${currentTheme.card} rounded-2xl shadow-coffee-lg p-6 mb-6 mt-6`}>
            <h2 className={`text-lg font-semibold ${currentTheme.text} mb-4`}>Contact Information</h2>
            <div className="space-y-2">
              {card.showEmail !== false && card.email && (
                <a href={`mailto:${card.email}`} className={`block text-base ${currentTheme.subtext} hover:opacity-80 transition-colors`}>{card.email}</a>
              )}
              {card.showPhone !== false && card.phoneNumber && (
                <a href={`tel:${card.phoneNumber}`} className={`block text-base ${currentTheme.subtext} hover:opacity-80 transition-colors`}>{card.phoneNumber}</a>
              )}
              {card.showAddress !== false && address && (
                <p className={`text-base ${currentTheme.subtext}`}>{address}</p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-current/10 grid grid-cols-2 gap-3">
              <button onClick={downloadVCard} className={`${currentTheme.accent} text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 hover:opacity-90`}>Save Contact</button>
              <button onClick={handleShare} className={`border-2 border-current ${currentTheme.subtext} hover:opacity-80 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2`}>{copied ? 'Link Copied!' : 'Share Card'}</button>
            </div>
            {card.hasResume && (
              <a href={getResumeUrlByUsername(card.username)} target="_blank" rel="noopener noreferrer" className={`block text-center ${currentTheme.accent} text-white font-medium py-2.5 px-4 rounded-lg transition-colors mt-3`}>View Resume</a>
            )}
          </div>
        )}

        {card.showContactBox === false && (
          <div className={`${currentTheme.card} rounded-2xl shadow-coffee-lg p-4 mb-6 mt-6`}>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={downloadVCard} className={`${currentTheme.accent} text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 hover:opacity-90`}>Save Contact</button>
              <button onClick={handleShare} className={`border-2 border-current ${currentTheme.subtext} hover:opacity-80 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2`}>{copied ? 'Link Copied!' : 'Share Card'}</button>
            </div>
            {card.hasResume && (
              <a href={getResumeUrlByUsername(card.username)} target="_blank" rel="noopener noreferrer" className={`block text-center ${currentTheme.accent} text-white font-medium py-2.5 px-4 rounded-lg transition-colors mt-3`}>View Resume</a>
            )}
          </div>
        )}

        {/* Legacy "Contact me" form removed. Owners now configure their own
            contact form as a contact_form-typed link in /my-card with up to
            four custom qualifying fields on Pro. The link renders inline in
            the My Links section above. */}

        {/* Save-my-card QR — sits at the bottom of the card content. Tapping
            opens the public card URL in a new tab so admins reviewing the
            preview can jump to the live page; visitors get a scannable QR. */}
        <div className="mt-6">
          <CardQR
            url={typeof window !== 'undefined' ? `${window.location.origin}/card/${card.username}` : `/card/${card.username}`}
            theme={currentTheme}
          />
        </div>

        {/* Footer — hidden in preview mode */}
        {!preview && !isPro && (
          <div className="text-center mt-8 space-y-2">
            {isSignedIn ? (
              <Link to="/my-card" className={`block text-sm font-medium ${currentTheme.subtext} hover:opacity-80 transition-opacity`}>Create Your Card</Link>
            ) : (
              <SignUpButton mode="modal">
                <button className={`text-sm font-medium ${currentTheme.subtext} hover:opacity-80 transition-opacity`}>Create Your Card</button>
              </SignUpButton>
            )}
            <a href="/" className="block text-[10px] font-mono text-coffee-400 tracking-eyebrow hover:text-coffee-600 transition-colors">powered by whobrew</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardRenderer;
